import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { SecondaryStats } from '@athleticnet/athletic-live-api-models/lib/models/shared/secondary-stats.interface';
import { TertiaryStats } from '@athleticnet/athletic-live-api-models/lib/models/shared/tertiary-stats.interface';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Subscription } from 'rxjs';
import { SecondaryStatsComponent } from 'src/app/event/secondary-stats/secondary-stats.component';
import { TertiaryStatsComponent } from '../../event/tertiary-stats/tertiary-stats.component';
import { CurrentMeetService } from '../services/current-meet.service';

@Component({
  selector: 'app-results-grid-row',
  templateUrl: './results-grid-row.component.html',
  styleUrl: './results-grid-row.component.scss',
  imports: [SecondaryStatsComponent, TertiaryStatsComponent, FontAwesomeModule, CommonModule, NgOptimizedImage],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultsGridRowComponent {
  // Primary Column Data
  @Input() firstName!: string;
  @Input() lastName?: string = '';
  @Input() teamLogoSrc?: string;
  @Input() teamName!: string;
  @Input() profilePicSrc?: string;
  @Input() hideProfilePic: boolean = false;
  @Input() hideTeamLogo: boolean = false;
  @Input() forceGap: boolean = true; // If this meet has logos (only when there is just a teamName) a gap will be added to the left of the row
  @Input() putTertiaryInTeamPlace: boolean = false; // If true, the tertiary stats will be placed in the team column, only used for team pages, also the page must be mobile not desktop view
  @Input() teamRankNumber?: number; // If set, the team rank will be displayed

  // Secondary Column Data
  @Input() showSecondary: boolean = true;
  @Input() secondaryResult?: SecondaryStats; // Set if there will be the SecondaryStatsComponent
  @Input() secondaryRawSubTitleValue?: string; // Set for the raw value of the secondary title
  @Input() secondaryValue!: string; // Set for the display value of the primary secondary title
  @Input() verticalJumps?: string;
  @Input() onlySecondaryValue: boolean = false;

  // Tertiary Column Data

  @Input() showTertiary: boolean = true;
  @Input() tertiaryResult!: TertiaryStats;
  @Input() showSeed: boolean = false;
  @Input() showHeat: boolean = false;
  @Input() showNotes: boolean = false;
  @Input() showSecondaryStats: boolean = false;
  @Input() showLapsToGo: boolean = false;
  @Input() showAthleteStats: boolean = false;
  @Input() showRecords: boolean = true;
  @Input() showCombinedPoints: boolean = true;
  @Input() showGap: boolean = false;
  @Input() showWind: boolean = true;
  @Input() primaryEventGroup: string = '';

  // Additional details to determine global meet options
  allowLogo: boolean = false;
  allowProfilePic: boolean = false;
  displayLogo: boolean = false;
  displayProfilePic: boolean = false;
  numOfLogos: number = 0;

  meetUpdateSub?: Subscription;
  reComputeLogos: boolean = true;
  showConversions: boolean = true;

  constructor(private currentMeetService: CurrentMeetService) {}

  ngOnInit() {
    // Only update the logo check if the meet has logos or profile pics
    this.meetUpdateSub = this.currentMeetService.updateMeet.subscribe((meet) => {
      if (meet) {
        this.showConversions = meet.showConversions ?? true;
        if (this.allowLogo != meet.hasLogos || this.allowProfilePic != meet.hasProfilePics) {
          this.reComputeLogos = true;
        }
        this.allowLogo = meet.hasLogos;
        this.allowProfilePic = meet.hasProfilePics;
      }
      if (this.reComputeLogos) {
        this.reComputeLogos = false;
        this.computeLogoCheck();
      }
    });

    // Ensure secondary conversion doesn't match secondaryValue
    if (this.secondaryRawSubTitleValue && this.secondaryRawSubTitleValue === this.secondaryValue) {
      this.secondaryRawSubTitleValue = '';
    } else if (
      this.showConversions &&
      this.secondaryResult &&
      this.secondaryResult.conversion &&
      this.secondaryResult.conversion === this.secondaryValue
    ) {
      this.secondaryResult.conversion = '';
    }
  }

  ngOnDestroy() {
    if (this.meetUpdateSub) {
      this.meetUpdateSub.unsubscribe();
    }
  }

  computeLogoCheck() {
    this.displayLogo = this.allowLogo && !!this.teamLogoSrc && !this.hideTeamLogo;
    this.displayProfilePic = this.allowProfilePic && !this.hideProfilePic;

    if (this.teamLogoSrc?.slice(-4) === '=s80') {
      this.teamLogoSrc = this.teamLogoSrc + '-c';
    }
    if (this.profilePicSrc?.slice(-4) === '=s80') {
      this.profilePicSrc = this.profilePicSrc + '-p';
    }

    if (
      (this.displayLogo && this.displayProfilePic) ||
      (this.displayProfilePic && this.forceGap && this.allowLogo && !this.hideTeamLogo)
    ) {
      this.numOfLogos = 2;
    } else {
      this.numOfLogos =
        this.displayLogo || this.displayProfilePic || (this.forceGap && this.allowLogo && !this.hideTeamLogo) ? 1 : 0;
    }
  }

  logData(data: any) {
    console.log('log', data);
  }

  composeInitials() {
    if (this.firstName && this.lastName) {
      if (this.firstName[0] && this.lastName[0]) {
        return this.firstName[0] + this.lastName[0];
      }
    }
    return '';
  }
}
